import React, { useContext, useEffect, useMemo } from "react";
import { Field, FormArray, FormGroup, GenericComponent, PInput } from "../../models/models";
import { AdvisoryText } from "../../../AdvisoryText";
import { FormContext } from "../../context/formContext";
import { ErrorMessage, useFormikContext } from "formik";
import FieldComponent from "./FieldComponent";
import FormArrayComponent from "./FormArrayComponent";

export default function FormGroupCompnent({
  parentKey,
  elementKey,
  label,
  warn,
  parentValues,
  hideExpression,
  customWrapper,
  warnType,
  fields,
}: {
  parentKey: string;
  elementKey?: string;
  label?: string;
  warn?: string;
  parentValues: any;
  hideExpression?: (formContext: any, values: any, model: any) => boolean;
  warnType?: "warning" | "information" | "error" | "announcement" | "fieldadv";
  customWrapper?: React.FC<any>;
  fields: (Field | FormGroup | FormArray | PInput | GenericComponent)[];
  test?: [nameString: string, valueFunction: (value: any) => boolean];
}) {
  const { values, setFieldValue } = useFormikContext();

  const context = useContext(FormContext);

  const CustomWrapper = customWrapper;
  const nextParentKey = useMemo(() => {
    return [parentKey, elementKey].filter((x) => x !== "" && x != null).join(".");
  }, [parentKey, elementKey]);

  const nextParentValues = useMemo(() => {
    const value = elementKey != null && elementKey != "" ? parentValues[elementKey] : parentValues;
    return value;
  }, [elementKey, parentValues]);

  useEffect(() => {
    if (hideExpression != null) {
      const isHidden = hideExpression(context.formContextStore, values, parentValues);
      // Only update the state if `isHidden` differs from `hidden`
      if (isHidden !== parentValues[elementKey + "Hidden"]) {
        setFieldValue(nextParentKey + "Hidden", isHidden);
      }
    }
  }, [hideExpression, values, parentValues, context]);

  const hidden = useMemo(() => parentValues[elementKey + "Hidden"], [parentValues[elementKey + "Hidden"]]);

  const fieldsOutput = () => {
    return fields.map((f) => {
      switch (f.type) {
        case "formGroup":
          f as FormGroup;
          return (
            <FormGroupCompnent
              key={f.key}
              parentKey={nextParentKey}
              parentValues={nextParentValues}
              elementKey={f.key}
              label={f.label}
              fields={f.fields}
              hideExpression={f.hideExpression}
              customWrapper={f.customWrapper}
            />
          );
        case "formArray":
          f as FormArray;
          return (
            <FormArrayComponent
              key={f.key}
              parentKey={nextParentKey}
              parentValues={nextParentValues}
              rest={f.props}
              elementKey={f.key}
              label={f.label}
              fields={f.fields}
              hideExpression={f.hideExpression}
              childModel={f.childModel}
              autoAddField={f.autoAddField}
              mutateValues={f.mutateValues}
            />
          );
        case "field":
          f as Field;
          return (
            <>
              <FieldComponent
                key={f.key}
                parentKey={nextParentKey}
                parentValues={nextParentValues}
                elementKey={f.key}
                fieldType={f.fieldType}
                label={f.label}
                warn={f.warn}
                props={f.props}
                expressionProps={f.expressionProps}
                hideExpression={f.hideExpression}
              />
              <div className={f.props?.advisorytextcss}>{f.props?.advisorytext}</div>
            </>
          );

        case "pInput":
          f as PInput;
          return <div className={f.props?.css}>{f.label}</div>;
          break;
        case "component": {
          f as GenericComponent;
          const Body = f.body;
          return <Body values={values} />;
        }
      }
    });
  };

  return (
    <>
      {!hidden && (
        <div>
          {(label || warn) && (
            <div className="py-2 ">
              {label && (
                <h4 className="text-purple flex align-items-center border-bottom-2">
                  <span className="header-icon p-3 mr-1 mt-0"></span>
                  <span>{label}</span>
                </h4>
              )}
              {warn && <AdvisoryText type={warnType ?? "warn"} label={warn} />}
            </div>
          )}

          {CustomWrapper != null ? <CustomWrapper>{fieldsOutput()}</CustomWrapper> : fieldsOutput()}
          {nextParentKey && (
            <ErrorMessage name={nextParentKey + "Test"} render={(msg) => <div className="error">{typeof msg == "string" && msg}</div>} />
          )}
        </div>
      )}
    </>
  );
}
